/**
 * Every screen has an address: app.liyumanagement.com/pay is Pay fees.
 *
 * The address only says which screen opens first. Who you are and what you may
 * see still come from the signed token and the Firestore rules, so typing
 * somebody else's screen into the bar shows you your own Home and nothing more.
 *
 * The list is written out rather than read from the sidebar so the hosting
 * rewrites in firebase.json and _redirects can be checked against it: a screen
 * missing there is a link that answers 404, and the test says which.
 */

export const SCREENS = [
  "home",
  "pay",
  "repairs",
  "notices",
  "chat",
  "give",
  "units",
  "books",
  "arrears",
  "journal",
  "chapa",
  "advertisements",
  "campaigns",
  "notifications",
  "payments",
] as const;

export type Screen = (typeof SCREENS)[number];

export function isScreen(value: string | null | undefined): value is Screen {
  return (SCREENS as readonly string[]).includes(value ?? "");
}

/**
 * The screen an address names, or null for one that names none (`/app.html`,
 * `/`, or a word that is not a screen).
 */
export function screenAt(pathname: string): Screen | null {
  const word = pathname.replace(/^\/+|\/+$/g, "");
  return isScreen(word) ? word : null;
}

/**
 * The address for a view. A view inside a screen (one notification, one of the
 * committee's charts) keeps the address of the screen it sits in, so a link
 * never points somewhere a reload cannot draw.
 */
export function pathFor(view: string): string {
  if (view.startsWith("notification:")) return "/notifications";
  if (view.startsWith("analytics-")) return "/home";
  return isScreen(view) ? `/${view}` : "/home";
}

/** The sign-in page, remembering where the person was headed. */
export function signInPath(screen: Screen | null): string {
  return screen === null || screen === "home" ? "/" : `/?to=${screen}`;
}

/**
 * Where to go once signed in: the screen asked for, if it is one, and the app's
 * Home otherwise. Only a known screen word is ever followed, so a link cannot
 * use this to send somebody off the site.
 */
export function afterSignIn(search: string): string {
  const to = new URLSearchParams(search).get("to");
  return isScreen(to) ? `/${to}` : "/home";
}
